import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { UserPlus, Radio } from 'lucide-react';

const ROLE_OPTIONS = [
  { value: 'brand', label: 'Brand', desc: 'Upload and manage ad media' },
  { value: 'streamer', label: 'Streamer', desc: 'Trigger ads during live streams' },
];

export default function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [roles, setRoles] = useState(['brand']);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const toggleRole = (role) => {
    setRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) {
      toast.error('Please fill in all fields');
      return;
    }
    if (roles.length === 0) {
      toast.error('Select at least one role');
      return;
    }
    setLoading(true);
    try {
      await register(name, email, password, roles);
      toast.success('Account created!');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6">
      <div className="w-full max-w-md animate-fade-in-up">
        <div className="flex items-center gap-3 mb-10">
          <div className="w-10 h-10 bg-[#FF3B30] flex items-center justify-center">
            <Radio className="w-5 h-5 text-white" />
          </div>
          <h1 className="font-heading text-3xl font-black tracking-tighter text-white uppercase">AYO<span className="text-gray-500 ml-1 font-medium text-lg normal-case">source</span></h1>
        </div>

        <h2 className="font-heading text-2xl font-bold text-white mb-2">Create Account</h2>
        <p className="text-gray-500 text-sm mb-8">Join the source-integrated ad platform</p>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <Label className="text-xs font-mono tracking-widest uppercase text-gray-500">Name</Label>
            <Input
              data-testid="register-name-input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              className="bg-black/40 border-white/10 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] text-white placeholder:text-gray-600 h-12"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-mono tracking-widest uppercase text-gray-500">Email</Label>
            <Input
              data-testid="register-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@stream.com"
              className="bg-black/40 border-white/10 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] text-white placeholder:text-gray-600 h-12"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-mono tracking-widest uppercase text-gray-500">Password</Label>
            <Input
              data-testid="register-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Min 6 characters"
              className="bg-black/40 border-white/10 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] text-white placeholder:text-gray-600 h-12"
            />
          </div>

          <div className="space-y-3">
            <Label className="text-xs font-mono tracking-widest uppercase text-gray-500">Select Roles</Label>
            {ROLE_OPTIONS.map((opt) => (
              <label
                key={opt.value}
                className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-all ${
                  roles.includes(opt.value) ? 'border-[#FF3B30]/40 bg-[#FF3B30]/5' : 'border-white/10 bg-white/[0.02]'
                }`}
              >
                <Checkbox
                  data-testid={`register-role-${opt.value}`}
                  checked={roles.includes(opt.value)}
                  onCheckedChange={() => toggleRole(opt.value)}
                  className="mt-0.5 border-white/20 data-[state=checked]:bg-[#FF3B30] data-[state=checked]:border-[#FF3B30]"
                />
                <div>
                  <span className="text-sm font-semibold text-white">{opt.label}</span>
                  <p className="text-xs text-gray-500 mt-0.5">{opt.desc}</p>
                </div>
              </label>
            ))}
          </div>

          <Button
            data-testid="register-submit-button"
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-[#FF3B30] hover:bg-[#D70015] text-white font-bold uppercase tracking-widest rounded-none transition-all duration-200"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <span className="flex items-center gap-2"><UserPlus className="w-4 h-4" /> Create Account</span>
            )}
          </Button>
        </form>

        <p className="text-center text-gray-500 text-sm mt-8">
          Already have an account?{' '}
          <Link to="/login" data-testid="go-to-login-link" className="text-[#FF3B30] hover:underline font-medium">Sign In</Link>
        </p>
      </div>
    </div>
  );
}
